import * as React from "react"

import { cn } from "@/lib/utils"
import { Badge } from "./badge"

// A space is flagged "Few Left" once free capacity drops to a quarter of the
// room's total, or to 5 seats — whichever bites first, so small rooms (a 4-seat
// meeting room) don't sit on "Available" until the moment they're full.
const FEW_LEFT_RATIO = 0.25;
const FEW_LEFT_ABSOLUTE = 5;

/**
 * Derives the availability status from free seats vs. total capacity.
 * Returns null when seatsLeft is unknown — callers should render nothing
 * rather than imply availability we haven't actually looked up.
 */
export function getAvailabilityStatus(seatsLeft, capacity) {
  if (seatsLeft === null || seatsLeft === undefined || Number.isNaN(Number(seatsLeft))) {
    return null;
  }
  const left = Number(seatsLeft);
  if (left <= 0) return 'sold_out';

  const total = Number(capacity);
  const ratioThreshold = total > 0 ? total * FEW_LEFT_RATIO : Infinity;
  return left <= Math.max(Math.min(ratioThreshold, FEW_LEFT_ABSOLUTE), 1) ? 'few_left' : 'available';
}

const STATUS_CONFIG = {
  available: { label: 'Available', variant: 'available' },
  few_left: { label: 'Few Left', variant: 'limited' },
  sold_out: { label: 'Sold Out', variant: 'destructive' },
};

/**
 * The status pill shown over a venue/space card image (top-left in the design).
 * Pass either an explicit `status`, or `seatsLeft`/`capacity` to have it derived.
 * Renders nothing when neither is available, so we never imply live
 * availability we haven't actually looked up.
 */
export function AvailabilityBadge({ status, seatsLeft, capacity, className, ...props }) {
  const resolved = status || getAvailabilityStatus(seatsLeft, capacity);
  const config = resolved && STATUS_CONFIG[resolved];
  if (!config) return null;

  return (
    <Badge
      variant={config.variant}
      shape="pill"
      className={cn("px-2.5 py-1 text-[11px] font-semibold", className)}
      {...props}
    >
      {config.label}
    </Badge>
  );
}

/**
 * The neutral seat-count pill shown over a card image (top-right in the design).
 * Defaults to total capacity wording; pass `remaining` only where the number
 * really is live availability rather than the room's total size.
 */
export function SeatsBadge({ count, remaining = false, className, ...props }) {
  if (count === null || count === undefined || Number.isNaN(Number(count))) return null;
  const n = Number(count);
  if (n <= 0) return null;

  return (
    <Badge
      variant="surface"
      shape="pill"
      className={cn("px-2.5 py-1 text-[11px] font-medium", className)}
      {...props}
    >
      {n} {n === 1 ? 'seat' : 'seats'}{remaining ? ' left' : ''}
    </Badge>
  );
}
